import { extractExpressionPaths, topologicalSort } from '@examdojo/core/widget';
import type {
  ElementDef,
  ElementDisplayConfig,
  FeatureType,
  LabelElementParams,
  LineElementParams,
  PointElementParams,
  PolygonElementParams,
  PrimitiveElementDef,
} from '../graphpad.model';
import { isFeatureElementDef, isFunctionElementDef, isPrimitiveElementDef } from '../graphpad.model';
import type { CreatedElement, ElementContext, ElementCreator, FunctionElement } from './element.model';
import { isFunctionElement } from './element.model';
import { getPositionDependencies } from './primitives/position';

/**
 * ElementResolver handles dependency resolution and element creation orchestration.
 * It ensures elements are created in the correct order based on their dependencies.
 */
export class ElementResolver {
  private readonly creators: Map<string, ElementCreator> = new Map();

  registerCreator(creator: ElementCreator): void {
    creator.types.forEach((type) => {
      this.creators.set(type, creator);
    });
  }

  registerCreators(creators: ElementCreator[]): void {
    creators.forEach((creator) => this.registerCreator(creator));
  }

  /**
   * Topologically sort elements by dependencies.
   * Returns element IDs in the order they should be created.
   */
  resolveDependencies(elements: Record<string, ElementDef>): string[] {
    const items = new Map(Object.entries(elements));
    return topologicalSort(items, (id, def) => this.extractDependencies(id, def, elements));
  }

  /**
   * Create a single element using the appropriate creator.
   * For features, validates that the parent function supports this feature type.
   */
  createElement(
    id: string,
    def: ElementDef,
    display: ElementDisplayConfig | undefined,
    context: ElementContext,
  ): CreatedElement {
    const creator = this.creators.get(def.type);
    if (!creator) {
      throw new Error(
        `Unknown element type: ${def.type}. Available types: ${Array.from(this.creators.keys()).join(', ')}`,
      );
    }

    // Validate feature against parent's availableFeatures
    if (isFeatureElementDef(def)) {
      this.validateFeature(id, def.type as FeatureType, def.params, context);
    }

    return creator.create(id, def, display, context);
  }

  getRegisteredTypes(): string[] {
    return Array.from(this.creators.keys());
  }

  /**
   * Extract all dependency IDs from an element definition.
   */
  private extractDependencies(_id: string, def: ElementDef, allElements: Record<string, ElementDef>): string[] {
    const deps: string[] = [];

    if (!('params' in def) || !def.params) {
      return deps;
    }

    // Standard `of` dependency (features and handles)
    if ('of' in def.params) {
      const ofValue = def.params['of'];
      if (Array.isArray(ofValue)) {
        deps.push(...(ofValue as string[]));
      } else if (typeof ofValue === 'string') {
        deps.push(ofValue);
      }
    }

    // Primitive element dependencies
    if (isPrimitiveElementDef(def)) {
      deps.push(...this.extractPrimitiveDependencies(def, allElements));
    }

    // Feature element ValueExpression dependencies (e.g. xStart: "f-roots.x0")
    if (isFeatureElementDef(def)) {
      for (const [key, val] of Object.entries(def.params)) {
        if (key === 'of' || typeof val !== 'string') {
          continue;
        }
        for (const path of extractExpressionPaths(val)) {
          const dotIndex = path.indexOf('.');
          const elementId = dotIndex > 0 ? path.substring(0, dotIndex) : path;
          deps.push(elementId);
        }
      }
    }

    // Function element ValueExpression dependencies (e.g. a: "g.a")
    if (isFunctionElementDef(def) && def.params) {
      for (const [, val] of Object.entries(def.params)) {
        if (typeof val !== 'string') {
          continue;
        }
        for (const path of extractExpressionPaths(val)) {
          const dotIndex = path.indexOf('.');
          const elementId = dotIndex > 0 ? path.substring(0, dotIndex) : path;
          deps.push(elementId);
        }
      }
    }

    return deps;
  }

  /**
   * Extract element ID dependencies from a primitive element definition.
   * Switches on def.type to handle each primitive's params structure.
   */
  private extractPrimitiveDependencies(def: PrimitiveElementDef, allElements: Record<string, ElementDef>): string[] {
    const deps = new Set<string>();

    switch (def.type) {
      case 'label': {
        const params = def.params as LabelElementParams;
        if (params.of && params.of in allElements) {
          deps.add(params.of);
        }
        if (params.position) {
          getPositionDependencies(params.position, allElements).forEach((d) => deps.add(d));
        }
        break;
      }

      case 'point': {
        const params = def.params as PointElementParams;
        for (const val of [params.x, params.y]) {
          if (typeof val === 'string') {
            const paths = extractExpressionPaths(val);
            for (const path of paths) {
              const dotIndex = path.indexOf('.');
              const elementId = dotIndex > 0 ? path.substring(0, dotIndex) : path;
              if (elementId in allElements) {
                deps.add(elementId);
              }
            }
          }
        }
        break;
      }

      case 'line': {
        const params = def.params as LineElementParams;
        for (const pos of [params.from, params.to]) {
          getPositionDependencies(pos, allElements).forEach((d) => deps.add(d));
        }
        break;
      }

      case 'polygon': {
        const params = def.params as PolygonElementParams;
        for (const vertex of params.vertices) {
          getPositionDependencies(vertex, allElements).forEach((d) => deps.add(d));
        }
        break;
      }
    }

    return Array.from(deps);
  }

  /**
   * Validate that a feature is supported by its parent function(s).
   * @throws Error if the feature is not available for the parent function type
   */
  private validateFeature(
    featureId: string,
    featureType: FeatureType,
    params: Record<string, unknown> | undefined,
    context: ElementContext,
  ): void {
    // Skip validation for features that don't have a parent reference
    if (!params || !('of' in params)) {
      return;
    }

    const parentRefs = params['of'];
    const parentsToCheck = Array.isArray(parentRefs) ? parentRefs : [parentRefs];

    for (const parentId of parentsToCheck) {
      if (typeof parentId !== 'string') {
        continue;
      }

      const parentElement = context.elements.get(parentId);
      if (!parentElement || !isFunctionElement(parentElement)) {
        continue;
      }

      const parent = parentElement as FunctionElement;
      const availableFeatures = parent.availableFeatures;

      if (!availableFeatures.includes(featureType)) {
        throw new Error(
          `Feature '${featureType}' is not available for function type '${parent.type}'. ` +
            `Element '${featureId}' references '${parentId}'. ` +
            `Available features: ${availableFeatures.join(', ')}`,
        );
      }
    }
  }
}
